import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components
import Box from '../../shared/atoms/Box';
import Text from '../../shared/atoms/Text';
import Alert from '../../shared/molecules/Alert';
import BillingAddressCard from './BillingAddressCard';
import BillingAddressModal from './BillingAddressModal';
// Hooks
import useModal from '../../../hooks/useModal';
// Services
import {
  getBillingAddresses,
  deleteBillingAddress,
  setDefaultBillingAddress,
} from '../../../services/billingAddresses';

const DisplayBillingAddresses = ({ client, refresh, onChanged }) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const [editing, setEditing] = useState(null);
  const showConfirm = useModal();

  const load = async () => {
    setLoading(true);
    try {
      const rep = await getBillingAddresses(client.id);
      setAddresses(Array.isArray(rep) ? rep : []);
    } catch (err) {
      console.error('error getBillingAddresses', err);
      setAddresses([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [refresh]);

  const handleSetDefault = async (id) => {
    try {
      // api.jsx resolves the parsed body even on non-2xx (e.g. a 404 for a
      // stale/foreign id), so a missing `id` on the response means failure —
      // don't report success on an error body.
      const rep = await setDefaultBillingAddress(client.id, id);
      if (rep && rep.id) {
        onChanged();
      } else {
        setFeedback('error');
      }
    } catch (err) {
      console.error('error setDefaultBillingAddress', err);
      setFeedback('error');
    }
  };

  const handleDelete = async (id) => {
    try {
      const rep = await deleteBillingAddress(client.id, id);
      if (rep && rep.ok) {
        setFeedback('deleted');
        onChanged();
      } else {
        setFeedback('error');
      }
    } catch (err) {
      console.error('error deleteBillingAddress', err);
      setFeedback('error');
    }
  };

  const confirmDelete = (id) => {
    showConfirm({
      title: t('billingaddresses.modal.delete.title'),
      content: t('billingaddresses.modal.delete.text'),
      showModal: true,
      showButton: [
        { text: t('button.cancel'), variant: 'neutral' },
        {
          text: t('billingaddresses.actions.delete'),
          color: 'blue.electric',
          onClick: () => handleDelete(id),
        },
      ],
    });
  };

  return (
    <>
      {feedback === 'deleted' && (
        <Alert status="success" mb={5}>
          {t('billingaddresses.alerts.deleted')}
        </Alert>
      )}
      {feedback === 'error' && (
        <Alert status="danger" mb={5}>
          {t('billingaddresses.alerts.error')}
        </Alert>
      )}
      {!loading && !addresses.length ? (
        <Box justifyContent="center" alignItems="center" mb={4}>
          <Text variant="large" color="black" mb={2}>
            {t('billingaddresses.empty.title')}
          </Text>
          <Text>{t('billingaddresses.empty.text')}</Text>
        </Box>
      ) : (
        addresses.map((address) => (
          <BillingAddressCard
            key={address.id}
            address={address}
            onSetDefault={handleSetDefault}
            onEdit={setEditing}
            onDelete={confirmDelete}
          />
        ))
      )}
      <BillingAddressModal
        open={!!editing}
        onRequestClose={() => setEditing(null)}
        clientId={client.id}
        mode="edit"
        address={editing}
        onSuccess={() => {
          setEditing(null);
          onChanged();
        }}
      />
    </>
  );
};

export default DisplayBillingAddresses;
